import SignInForButton from "@galarson/Components/SignInForButton";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct & WidgetProps;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    signInGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`

            padding: .9375rem 0 0 0;
            border-bottom: 1px solid #e0e0e0;
            border-collapse: collapse;
            min-width: 320px; /* prevent overlap with second column */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                    padding: 15px;
                    `,
                    css`
                    padding: 15px;
                    `,
                    css`
                    `,
                    css`
                    `,
                    css`
                    `,
            ])}
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 2, 2, 2],
        css: css`
            padding: 0 0 .9375rem .9375rem !important;
            [data-test-selector="productListProductImage"] {
                
            }
            [data-test-selector="productImage"] img{
                width: 100%;
                height: auto;
                object-fit: contain;
            }
            [data-test-selector="productList_CompareCheckbox"] {
                margin: .5em 0;
                padding: 0;
                width: 100%;
                display: flex;
                justify-content: start;
                align-items: start;
            }
            [data-test-selector="availabilityByWarehouseLink"] span{
                color: #254351;
                font-family: 'Franklin Gothic Heavy';
                font-weight: 800;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                        padding: 0px !important;
                        [data-test-selector="productImage"] img{
                            width: 100%;
                            height: 100%;
                        }
                    `,
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                        padding: 0px !important;
                        [data-test-selector="productImage"] img{
                            width: 100%;
                            height: 100%;
                        }
                    `,
                    css`

                    `,
                    css`

                    `,
                    css`

                    `,
            ])}
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 10, 10, 10],
        css: css`
            padding-right: 10px;
            padding-left: 10px;
        `
    },
    infoGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-wrap: wrap;
            [data-test-selector="product_brand"] {
                [data-test-selector="brandLink"] {
                    ${TypographyStyle} {
                        font-family: "Franklin Gothic Book";
                        font-size: 15px;
                        font-weight: 400;
                        color: #2d2d2d;
                    }
                }
            }
            [data-test-selector="productDescriptionLink"] { 
                ${TypographyStyle} {
                    margin-bottom: 5px;
                    text-transform: capitalize;
                    color: ${getColor("text.main")} !important;
                    white-space: normal;
                    font-size: 24px;
                    line-height: 1.25em;
                    font-weight: normal;
                    font-family: 'Franklin Gothic Heavy';
                }
                :hover{
                    text-decoration: underline;
                }
            }
        `
    },
    actionsGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            text-align: end;
            [data-test-selector="priceIncludeVat"]{
                [data-test-selector="productPrice_unitNetPrice"] {
                    font-size: 18px;
                }
                [data-test-selector="productPrice_unitOfMeasureLabel"] {
                    font-size: 18px;
                    font-weight: 600;
                }
            }
            [data-test-selector="product_qtyOrdered-label"] {
                display: none;
            }
            [data-test-selector="product_qtyOrdered"] {
                border: 3px solid #02788c !important;
            }
        `
    },
    signInGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <GridItem {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={props.showCompare}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                />
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                        />
                        <SignInForButton />
                    </GridItem>
                    <GridItem {...styles.actionsGridItem}>
                        <ProductListActions showPrice={props.showPrice} showAddToList={props.showAddToList} />
                    </GridItem>
                    <GridItem {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default withProduct(ProductListProductCard);
